import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Event } from '@casetrace/shared';
import { isOAuthConnectedFlag } from '../../router/search-params';
import { createInitialFilterState } from './filter-panel-helpers';
import { useCaseRole } from '../../hooks/use-cases';
import { useCaseHighlights } from '../../hooks/use-case-highlights';
import { useToggleHighlight } from '../../hooks/use-toggle-highlight';

export function useCaseRoomPageState(caseId: string) {
  const { data: caseRole } = useCaseRole(caseId);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [filters, setFilters] = useState(createInitialFilterState);

  const { data: highlights } = useCaseHighlights(caseId);
  const toggleHighlight = useToggleHighlight(caseId);
  const highlightedEventIds = useMemo(() => new Set(highlights?.keys() ?? []), [highlights]);
  const starredCount = highlights?.size ?? 0;
  const canToggleHighlight = caseRole === 'owner' || caseRole === 'member';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!isOAuthConnectedFlag(params.get('connected'))) return;
    setIsSourcesOpen(true);
    params.delete('connected');
    const next = `${window.location.pathname}${params.size > 0 ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);

  const handleSelect = useCallback((event: Event) => {
    setSelectedEvent((prev) => (prev?.id === event.id ? null : event));
  }, []);

  const openEvent = useCallback((event: Event) => {
    setSelectedEvent(event);
  }, []);

  const deselectEvent = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  return {
    filters,
    setFilters,
    selectedEvent,
    isMembersOpen,
    setIsMembersOpen,
    isSourcesOpen,
    setIsSourcesOpen,
    highlightedEventIds,
    starredCount,
    canToggleHighlight,
    caseRole,
    toggleHighlight,
    handleSelect,
    openEvent,
    deselectEvent,
  };
}
